// Razorpay Checkout loader + a thin promise wrapper around the modal.
//
// Unlike Stripe (npm SDK + Elements), Razorpay Checkout is a hosted modal
// loaded from their CDN via a <script> tag. The public Key ID is NOT a build
// env var here — it comes from the backend create-order response (which reads
// it from admin settings), so a single deploy works across test/live keys.

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let scriptPromise: Promise<boolean> | null = null;

declare global {
  interface Window {
    Razorpay?: any;
  }
}

/**
 * Inject the Razorpay Checkout script once and resolve when `window.Razorpay`
 * is available. Subsequent calls reuse the same promise.
 */
export const loadRazorpayScript = (): Promise<boolean> => {
  if (typeof window !== 'undefined' && window.Razorpay) {
    return Promise.resolve(true);
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<boolean>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      if (window.Razorpay) resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      scriptPromise = null; // allow a retry on a later attempt
      resolve(false);
    };
    document.body.appendChild(script);
  });

  return scriptPromise;
};

export interface RazorpayCheckoutSuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface OpenRazorpayCheckoutParams {
  keyId: string;
  orderId: string;
  amount: number; // minor units, as returned by the backend
  currency: string;
  name?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  themeColor?: string;
}

/**
 * Open the Razorpay Checkout modal and resolve with the success payload, or
 * reject when the customer dismisses it or the payment fails. Callers then POST
 * the payload to the backend for signature/amount verification.
 */
export const openRazorpayCheckout = async (
  params: OpenRazorpayCheckoutParams
): Promise<RazorpayCheckoutSuccess> => {
  const loaded = await loadRazorpayScript();
  if (!loaded || !window.Razorpay) {
    throw new Error('Unable to load Razorpay Checkout. Please check your connection and try again.');
  }

  return new Promise<RazorpayCheckoutSuccess>((resolve, reject) => {
    const options: any = {
      key: params.keyId,
      order_id: params.orderId,
      amount: params.amount,
      currency: params.currency,
      name: params.name || 'Dasoon Eggs',
      description: params.description,
      prefill: params.prefill,
      theme: { color: params.themeColor || '#1890ff' },
      handler: (response: RazorpayCheckoutSuccess) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    };

    const rzp = new window.Razorpay(options);
    // Surface explicit payment failures (as opposed to a plain dismiss).
    rzp.on('payment.failed', (resp: any) => {
      reject(new Error(resp?.error?.description || 'Payment failed'));
    });
    rzp.open();
  });
};

/**
 * The storefront cannot know at build time whether Razorpay is configured — the
 * key id arrives per-order from the backend. This stays true (the loader/order
 * call surfaces real errors), kept for parity with the old isStripeConfigured().
 */
export const isRazorpayConfigured = (): boolean => true;
